import React from 'react';
import { useTranslation } from 'react-i18next';
import { OrderingSelectors, OrderingConstants, OrderingOperations } from 'polygon-ordering';
import { enqueueErrorSnackbar } from '../utils/snackbar';
import Text from './Text';
import TouchableOpacity from './TouchableOpacity';
import { paymentInfo } from '../utils/analytics';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import lodash from 'lodash';

import RadioCheck from './RadioCheck';
import SectionContainer from './SectionContainer';

const { updateSelectedPaymentMethods, resetSelectedPaymentMethods } = OrderingOperations;

const {
  getAvailablePaymentMethods,
  getEnabledPaymentMethods,
  getSaleType,
  getSelectedPaymentMethods,
  getTotalCoveredBySubPayments,
  getOrderTotals,
  getStagedPurchases,
  getGatewayType,
  getLocation,
} = OrderingSelectors;

const { PAYMENT_GATEWAY_PAYMENT_METHODS, PAYMENT_GATEWAY_TYPES, PAYMENT_METHOD } =
  OrderingConstants;
interface IProps {
  disabled?: boolean;
  children?: React.ReactNode;
  selected?: () => boolean;
  alreadySelected?: boolean;
  allowSelectionWhenUnavailable?: boolean;
  additionalBehaviour?: () => void;
  right?: React.ReactNode | null;
  subtitle?: React.ReactNode | null;
  label?: string;
  method: PAYMENT_METHOD;
}

const PaymentMethod: React.FC<IProps> = ({
  method,
  disabled,
  label,
  right,
  children,
  subtitle,
  additionalBehaviour,
  allowSelectionWhenUnavailable = false,
  selected,
  alreadySelected,
}) => {
  const checkoutInProgress = useAppSelector(state => state.checkoutInProgress);
  const saleType = useAppSelector(getSaleType);
  const selectedPaymentMethods = useAppSelector(getSelectedPaymentMethods);
  const totalCoveredBySubPayments = useAppSelector(getTotalCoveredBySubPayments);
  const availablePaymentMethods = useAppSelector(getAvailablePaymentMethods);
  const enabledPaymentMethods = useAppSelector(getEnabledPaymentMethods);
  const orderTotals = useAppSelector(getOrderTotals);
  const stagedPurchases = useAppSelector(getStagedPurchases);
  const location = useAppSelector(getLocation);
  // This isMultiPaymentAllowed means multi payment is enabled in PC
  const isMultiPaymentAllowed = useAppSelector(state => state.config.multiPaymentsAllowed);

  // This enableMultiPayments means whether user clicked on the button and enabled multi payment
  const enableMultiPayments = useAppSelector(state => state.enableMultiPayments);

  const gatewayType = useAppSelector(getGatewayType);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  if (!enabledPaymentMethods.includes(method)) {
    return null;
  }

  alreadySelected = !selected
    ? Boolean(lodash.find(selectedPaymentMethods, ['method', method]))
    : selected();

  const notAvailable = !availablePaymentMethods.includes(method);

  const totalCoveredAndNotSelected = totalCoveredBySubPayments && !alreadySelected;

  const hasMemberMethodSelected = selectedPaymentMethods.some(selectedPaymentMethod => {
    return !PAYMENT_GATEWAY_PAYMENT_METHODS.includes(selectedPaymentMethod.method);
  });

  const getDisabledByOtherSelections = () => {
    if (!hasMemberMethodSelected) {
      return false;
    }

    if (isMultiPaymentAllowed && enableMultiPayments) {
      return totalCoveredAndNotSelected;
    }

    return false;
  };

  const disabledByOtherSelections = getDisabledByOtherSelections();

  const renderChildren = () => {
    if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && method === PAYMENT_METHOD.CREDIT_CARD) {
      return undefined;
    }

    if (alreadySelected) {
      return children;
    }

    return undefined;
  };

  return (
    <div style={styles.mainContainer}>
      <SectionContainer style={styles.sectionContainer}>
        <TouchableOpacity
          onClick={e => {
            if (!alreadySelected) {
              paymentInfo({
                items: stagedPurchases.map(purchaseItem => {
                  return {
                    ...purchaseItem.item,
                    totalPrice: purchaseItem.moneyPrice,
                  };
                }),
                total: orderTotals!.discountedMoneyPrice,
                payment_method: method,
                store: location?.name,
              });
            }

            if ((e?.target as HTMLDivElement).tagName === 'INPUT') {
              if (!alreadySelected) {
                dispatch(
                  updateSelectedPaymentMethods({
                    paymentMethod: method,
                    alreadySelected,
                  }),
                ).catch(err => {
                  enqueueErrorSnackbar(err.message || err);
                });
              }
            } else {
              if (!enableMultiPayments) {
                dispatch(resetSelectedPaymentMethods({}));
              }

              dispatch(
                updateSelectedPaymentMethods({
                  paymentMethod: method,
                  alreadySelected,
                  multiPayment: enableMultiPayments,
                }),
              ).catch(err => {
                enqueueErrorSnackbar(err.message || err);
              });
            }
            if (additionalBehaviour) {
              additionalBehaviour();
            }
          }}
          disabled={
            checkoutInProgress ||
            disabled ||
            (notAvailable && !allowSelectionWhenUnavailable) ||
            disabledByOtherSelections
          }
          disabledStyle={styles.disabled}
          enabledDefault
          skipMouseEvent
          ariaLabel={label}
        >
          <div style={styles.topContainer}>
            <RadioCheck checked={alreadySelected} />

            {method === PAYMENT_METHOD.PAY_LATER ? (
              <Text themeKey="paymentMethod" style={styles.paymentType}>
                {label || t(`paymentMethod.${method}.${saleType}`)}
              </Text>
            ) : (
              <Text style={styles.paymentType} themeKey="paymentMethod">
                {label || t(`paymentMethod.${method}`)}
              </Text>
            )}
            {right == null ? <div /> : right}
          </div>

          <div>{subtitle}</div>
        </TouchableOpacity>

        {/* Doing this way because for tyro we do DOM Manipulation to inject the form and if we hide children conditionally based on alreadySelected then we don't get to see the credit card form */}
        <div>
          {gatewayType === PAYMENT_GATEWAY_TYPES.TYRO &&
            method === PAYMENT_METHOD.CREDIT_CARD &&
            children}
        </div>
        <div>{renderChildren()}</div>
      </SectionContainer>
    </div>
  );
};

const styles: Styles = {
  mainContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },

  icon: {
    margin: '3px 10px 0 0',
  },

  sectionContainer: {
    flex: 1,
  },

  topContainer: {
    paddingTop: 4,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  disabled: {
    opacity: 0.5,
    filter: 'grayscale(100%)',
  },
  paymentType: { marginRight: 'auto', paddingLeft: 10 },
};

export default PaymentMethod;
